import { useState, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import findIndex from 'lodash/findIndex';
import { useCurrentRefinements, useInfiniteHits } from 'react-instantsearch';
import { calculateContentWidth } from './helpers';
import { expandHitsByDivision } from './algoliaDivisionUtils';
import ProductChevronPopup from '../product/ProductChevronPopup';
import ProductGrid from '../product-search/shared/ProductGrid';
import { useProductActions } from '../product-search/shared/useProductActions';

function toSearchProduct(hit) {
  return {
    id: `${hit.ext_product_id}::${hit.division_id ?? ''}`,
    name: hit.product_name,
    supplierCode: hit.product_supplier_code,
    divisionId: hit.division_id,
    divisionName: hit.division_name,
    minPrice: hit.min_price,
    maxPrice: hit.max_price,
    currency: hit.currency,
    _raw: hit,
  };
}

function CustomInfiniteHits({ loading, order, full_order, onAddItem, onDeleteItem, divisions, size, sidebarMinWidth, range, isProductsPage, ...props }) {
  const { items, showMore, isLastPage, sendEvent } = useInfiniteHits(props);
  const { items: currentRefinementItems } = useCurrentRefinements({
    includedAttributes: ['division_ids'],
  });
  const [selectedItem, setSelectedItem] = useState(null);

  const contentWidth = calculateContentWidth(size.clientWidth, sidebarMinWidth) + 6;
  const activeDivisionIds = useMemo(() => {
    return currentRefinementItems
      .flatMap((item) => item.refinements || [])
      .map((refinement) => refinement.value)
      .filter(Boolean);
  }, [currentRefinementItems]);
  const displayItems = useMemo(() => {
    const convertedItems = expandHitsByDivision(items, divisions, activeDivisionIds);
    return convertedItems.length > 0 ? convertedItems : items;
  }, [items, divisions, activeDivisionIds]);
  const products = useMemo(() => displayItems.map(toSearchProduct), [displayItems]);

  const { isSelected, isItemLoading, handleClickProductAction, trackClickEvent } = useProductActions({
    order,
    fullOrder: isProductsPage ? null : full_order,
    onAddItem,
    onDeleteItem,
    loading,
    isProductsPage,
    eventPrefix: 'connected_plus',
    onSendConversionEvent: (item, label) => sendEvent('conversion', item, label, { eventSubtype: 'addToCart' }),
  });

  return (
    <>
      <ProductGrid
        products={products}
        isLastPage={isLastPage}
        onLoadMore={showMore}
        loading={loading}
        isProductsPage={isProductsPage}
        isSelected={(product) => isSelected(product._raw)}
        onClickProduct={(product) => {
          sendEvent('click', product._raw, 'Product Clicked');
          setSelectedItem(product._raw);
          trackClickEvent();
        }}
        onClickProductAction={(product) => {
          if (isItemLoading(product._raw)) return;
          handleClickProductAction(product._raw);
        }}
        contentWidth={contentWidth}
        range={range}
        productType="others"
        isItemLoading={(product) => isItemLoading(product._raw)}
      />
      {isEmpty(selectedItem) ? null : <ProductChevronPopup
        products={displayItems}
        productIndex={findIndex(displayItems, selectedItem)}
        isSelected={(item) => isSelected(item)}
        onClose={() => setSelectedItem(null)}
        closeOnClickOutside={true}
        handleLoadNextPage={showMore}
        isEnd={isLastPage && findIndex(displayItems, selectedItem) === displayItems.length - 1}
        onClickProductAction={() => handleClickProductAction(selectedItem, true)}
        isProductsPage={isProductsPage}
      />}
    </>
  );
}

export default CustomInfiniteHits;
